/**
 * Price Calculator - Handles pricing logic
 */
class ShopifyPriceCalculator {
  /**
   * @param {Product} product - The product object
   * @param {string} format - The format
   * @param {string} type - The purchase type
   * @returns {ProductVariant | null} The variant or null
   */
  getVariantByOptions(product, format, type) {
    if (!product.variants?.length) return null;

    return (
      product.variants.find(variant => {
        // Try format matching in option1 first, then option2
        let formatMatch = variant.option1
          ? variant.option1.toLowerCase().includes(format.toLowerCase())
          : false;

        if (!formatMatch) {
          formatMatch = variant.option2
            ? variant.option2.toLowerCase().includes(format.toLowerCase())
            : false;
        }

        let typeMatch = false;
        if (type === 'buy') {
          // For format matching, check if the entire option2 contains the type
          if (variant.option2) {
            // First check if option2 has the full format (like "Buy New")
            if (variant.option2.toLowerCase().includes(format.toLowerCase())) {
              typeMatch = true; // If option2 contains the format, it's a match
            } else {
              // Fallback to checking for "buy" in option2
              typeMatch = variant.option2.toLowerCase().includes('buy');
            }
          }
        } else if (type === 'rent' || type.startsWith('rent-')) {
          typeMatch = variant.option2
            ? variant.option2.toLowerCase().includes('rent')
            : false;
          if (typeMatch && type.includes('-')) {
            const requestedDays = type.split('-')[1];
            typeMatch =
              variant.option2 && requestedDays
                ? variant.option2.includes(requestedDays)
                : false;
          }
        }

        return formatMatch && typeMatch;
      }) || null
    );
  }

  /**
   * @param {Product} product - The product object
   * @param {string} format - The format
   * @returns {string|null} The lowest price or null
   */
  getLowestPriceForFormat(product, format) {
    if (!product.variants?.length) {
      return null;
    }

    // Format matching - try option1 first (preferred), then option2 if no matches
    let matchingVariants = product.variants.filter(variant => {
      return (
        variant.option1 &&
        variant.option1.toLowerCase().includes(format.toLowerCase())
      );
    });

    // If no matches in option1, try option2 (for cases where format is in option2)
    if (matchingVariants.length === 0) {
      matchingVariants = product.variants.filter(variant => {
        return (
          variant.option2 &&
          variant.option2.toLowerCase().includes(format.toLowerCase())
        );
      });
    }

    if (matchingVariants.length === 0) {
      return null;
    }

    const prices = matchingVariants
      .map(v => parseFloat(v.price))
      .filter(p => !isNaN(p) && p >= 0);

    if (prices.length === 0) {
      return null;
    }

    const lowestPrice = Math.min(...prices).toFixed(2);

    return lowestPrice;
  }

  /**
   * @param {Product} product - The product object
   * @returns {Object|null} Price range object or null
   */
  calculatePriceRange(product) {
    if (!product.variants?.length) return null;

    const allPrices = product.variants
      .map(v => parseFloat(v.price))
      .filter(p => !isNaN(p) && p >= 0);

    if (allPrices.length === 0) return null;

    const min = Math.min(...allPrices);
    const max = Math.max(...allPrices);

    return {
      min: min.toFixed(2),
      max: max.toFixed(2),
      range: min === max ? null : `${min.toFixed(2)} - ${max.toFixed(2)}`,
    };
  }
}

window.ProductSystem = window.ProductSystem || {};
window.ProductSystem.ShopifyPriceCalculator = ShopifyPriceCalculator;
